import React, { useEffect } from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import {
  CANCEL_LABEL,
  CERTIFICATION_LABEL,
  CONFIRM_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  DATE_ACHIEVEMENT_LABEL,
  MUST_BE_AFTER_THAN_LABEL,
  EDIT_CERTIFICATION_LABEL,
  FIELD_IS_REQUIRED_LABEL,
  INVALID_DATE_LABEL,
  VALIDITY_EXPIRATION_LABEL,
} from "../../../../../../../constants/Dictionary";
import { FileUploaderSizeEnum } from "../../../../../../../types/AppTypes";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CancelIcon } from "../../../../../../../icons/close.svg";
import { CalendarInput } from "../../../../../../../components/core/CalendarInput/CalendarInput";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import {
  CollaboratorCmSheet,
  CollaboratorCmSheetCertification,
} from "../../../../../../../types/api/collaborators.type";
import { FileUploader } from "../../../../../../../components/core/UploadFile/FileUploader";
import { faker } from "@faker-js/faker";
import { EditModalContent } from "../../../SchedaCM.styled";
import dayjs from "dayjs";
import { EN_DATE_FORMAT } from "../../../../../../../utils/date/date.utils";
import { TextBox } from "../../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { OperationEnum } from "../../../../../../../types/api/generic.type";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import { useUpdateCmSheetCertifications } from "../../../../../../../hooks/cmSheet/certifications.hook";
import {
  useDeleteCmSheetCertificationCertificate,
  useUpdateCmSheetCertificationCertificate,
} from "../../../../../../../hooks/cmSheet/certificationCertificate.hook";

type FormData = {
  deliveryDate?: Date | null;
  expirationDate?: Date | null;
  certificate: {
    file?: File | null;
    name?: string | null;
  } | null;
};

interface EditCollaboratorCertificationProps {
  cmSheetCertification: CollaboratorCmSheetCertification;
  isOpen: boolean;
  closeModal: () => void;
  cmSheetInfo: CollaboratorCmSheet;
}

export const EditCollaboratorCertification = ({
  cmSheetCertification,
  isOpen,
  closeModal,
  cmSheetInfo,
}: EditCollaboratorCertificationProps) => {
  const { updateCmSheetCertifications, isUpdating: updatingCertification } =
    useUpdateCmSheetCertifications(cmSheetInfo.id);

  const { updateCertificationCertificate, isUpdating: updatingCertificate } =
    useUpdateCmSheetCertificationCertificate(
      cmSheetInfo.id,
      cmSheetCertification.id,
    );

  const { deleteCertificationCertificate, isDeleting: deletingCertificate } =
    useDeleteCmSheetCertificationCertificate(cmSheetInfo.id);

  const loading =
    updatingCertification || updatingCertificate || deletingCertificate;

  const schema = yup.object({
    deliveryDate: yup
      .date()
      .typeError(`${INVALID_DATE_LABEL}`)
      .nullable()
      .when(["expirationDate"], {
        is: () => true,
        then: (field) => field.required(FIELD_IS_REQUIRED_LABEL),
      }),
    expirationDate: yup
      .date()
      .typeError(`${INVALID_DATE_LABEL}`)
      .nullable()
      .test(
        "is-after-deliveryDate",
        `La ${VALIDITY_EXPIRATION_LABEL} ${MUST_BE_AFTER_THAN_LABEL} ${DATE_ACHIEVEMENT_LABEL}.`,
        function (expirationDate) {
          const { deliveryDate } = this.parent;
          if (!expirationDate || !deliveryDate) return true;
          const oneDay = 24 * 60 * 60 * 1000;
          return expirationDate.getTime() - deliveryDate.getTime() >= oneDay;
        },
      ),
    certificate: yup
      .object({
        file: yup.mixed<File>().nullable(),
        name: yup.string().nullable(),
      })
      .nullable(),
  });
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    if (dirtyFields.deliveryDate || dirtyFields.expirationDate) {
      const body = [
        {
          operation: OperationEnum.UPDATE,
          entityId: cmSheetCertification.id,
          data: {
            ...(dirtyFields.deliveryDate && {
              deliveryDate: data.deliveryDate
                ? dayjs(data.deliveryDate).format(EN_DATE_FORMAT)
                : null,
            }),
            ...(dirtyFields.expirationDate && {
              expirationDate: data.expirationDate
                ? dayjs(data.expirationDate).format(EN_DATE_FORMAT)
                : null,
            }),
          },
        },
      ];
      await updateCmSheetCertifications(body, DATA_UPDATED_SUCCESS_LABEL);
    }
    if (dirtyFields.certificate) {
      if (data.certificate && data.certificate.file) {
        await updateCertificationCertificate({ file: data.certificate.file });
      } else {
        await deleteCertificationCertificate(cmSheetCertification.id);
      }
    }
    closeModal();
  };

  useEffect(() => {
    if (cmSheetCertification) {
      reset({
        deliveryDate: cmSheetCertification.deliveryDate
          ? dayjs(cmSheetCertification.deliveryDate).toDate()
          : null,
        expirationDate: cmSheetCertification.expirationDate
          ? dayjs(cmSheetCertification.expirationDate).toDate()
          : null,
        certificate: {
          name: cmSheetCertification.fileName,
        },
      });
    } else {
      reset({
        deliveryDate: null,
        expirationDate: null,
        certificate: null,
      });
    }
  }, [cmSheetCertification, isOpen]);

  const deliveryDate = watch("deliveryDate");

  return (
    <Modal
      label={EDIT_CERTIFICATION_LABEL}
      fullSize={false}
      isOpen={isOpen}
      rightButton={
        <Button
          icon={<CheckIcon />}
          form="updateCertification"
          label={CONFIRM_LABEL}
          disabled={!isDirty || loading}
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={() => closeModal()}
        />
      }
    >
      <Loader loading={loading}>
        <EditModalContent>
          <TextBox
            label={CERTIFICATION_LABEL}
            text={cmSheetCertification.certification.name}
          />
          <form id="updateCertification" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-row">
              <FormField error={errors.deliveryDate?.message}>
                <Controller
                  name="deliveryDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={DATE_ACHIEVEMENT_LABEL}
                      {...field}
                      maxDate={new Date()}
                    />
                  )}
                />
              </FormField>
              <FormField error={errors.expirationDate?.message}>
                <Controller
                  name="expirationDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={VALIDITY_EXPIRATION_LABEL}
                      minDate={
                        deliveryDate && !isNaN(deliveryDate.getTime())
                          ? deliveryDate
                          : undefined
                      }
                      {...field}
                    />
                  )}
                />
              </FormField>
            </div>
            <FormField error={errors.certificate?.message}>
              <div className="file-uploader-container">
                <Controller
                  name="certificate"
                  control={control}
                  render={({ field }) => {
                    return (
                      <FileUploader
                        size={FileUploaderSizeEnum.REGULAR}
                        onFileUpload={(file: File) => {
                          field.onChange({ file: file, name: file.name });
                        }}
                        value={field.value?.file || null}
                        fileName={field.value?.name! ?? ""}
                        fileSize={
                          cmSheetCertification.fileName
                            ? faker.number.int({ min: 1000, max: 5000 })
                            : undefined
                        }
                        handleDeleteFile={() => {
                          field.onChange({ file: "", name: "" });
                        }}
                      />
                    );
                  }}
                />
              </div>
            </FormField>
          </form>
        </EditModalContent>
      </Loader>
    </Modal>
  );
};
